/**
 * Reglas de formulario de *Configuración → Empresa*.
 *
 * Las opciones de cada `select` son las del panel anterior
 * (`views/menu/empresa/index.php`), con los mismos valores que esperan las
 * columnas `enum` de la base de datos.
 */

import { formatCurrency, formatNumber } from '@/lib/utils';

import type {
  ConfigCompany,
  ConfigCompanyPayload,
  EmpresaConfig,
  EmpresaFormulario,
  EmpresaPayload,
  PlanContratado,
  PlanDisponible,
} from '../empresa.types';

export interface Opcion<T extends string = string> {
  valor: T;
  etiqueta: string;
}

export const SI_NO: readonly Opcion<'SI' | 'NO'>[] = [
  { valor: 'SI', etiqueta: 'Sí' },
  { valor: 'NO', etiqueta: 'No' },
];

export const CIERRES: readonly Opcion<'blind_closure' | 'informed_closure'>[] = [
  { valor: 'blind_closure', etiqueta: 'Cierre a ciegas' },
  { valor: 'informed_closure', etiqueta: 'Cierre informado' },
];

/** Bandera 0/1 (columnas `tinyint`). */
export const BANDERA: readonly Opcion<'0' | '1'>[] = [
  { valor: '1', etiqueta: 'Sí' },
  { valor: '0', etiqueta: 'No' },
];

/** Impresión de CPE múltiple. */
export const IMPRESION_CPE: readonly Opcion<'0' | '1'>[] = [
  { valor: '0', etiqueta: 'Detallado' },
  { valor: '1', etiqueta: 'Agrupado' },
];

/** NFC en el POS. */
export const NFC: readonly Opcion<'0' | '1'>[] = [
  { valor: '0', etiqueta: 'No (sin NFC)' },
  { valor: '1', etiqueta: 'Sí (con NFC)' },
];

export const FIDELIDADES: readonly Opcion<'no' | 'cardplus' | 'bonus'>[] = [
  { valor: 'no', etiqueta: 'Sin sistema de fidelidad' },
  { valor: 'cardplus', etiqueta: 'CardPlus' },
  { valor: 'bonus', etiqueta: 'Bonus' },
];

export const VISIBILIDAD: readonly Opcion<'libre' | 'menu' | 'ambos'>[] = [
  { valor: 'libre', etiqueta: 'Sólo transacciones libres' },
  { valor: 'menu', etiqueta: 'Sólo menú' },
  { valor: 'ambos', etiqueta: 'Ambos' },
];

export const CREDITOS: readonly Opcion<'libre' | 'flota' | 'ambos'>[] = [
  { valor: 'libre', etiqueta: 'Sólo transacciones libres' },
  { valor: 'flota', etiqueta: 'Sólo flota' },
  { valor: 'ambos', etiqueta: 'Ambos' },
];

export const MODOS_DESCUENTO: readonly Opcion<'NINGUNO' | 'CPE' | 'CREDITO' | 'MIXTO'>[] = [
  { valor: 'NINGUNO', etiqueta: 'Ninguno' },
  { valor: 'CPE', etiqueta: 'CPE' },
  { valor: 'CREDITO', etiqueta: 'Crédito' },
  { valor: 'MIXTO', etiqueta: 'Mixto' },
];

export const TIPOS_DOC_AUTOTICKETING: readonly Opcion<'boleta' | 'nota_venta'>[] = [
  { valor: 'boleta', etiqueta: 'Boleta' },
  { valor: 'nota_venta', etiqueta: 'Nota de venta' },
];

export const MODOS_TRABAJO: readonly Opcion<'POS' | 'MIXTO'>[] = [
  { valor: 'POS', etiqueta: 'POS' },
  { valor: 'MIXTO', etiqueta: 'Mixto' },
];

export const IDIOMAS: readonly Opcion<'español' | 'ingles'>[] = [
  { valor: 'español', etiqueta: 'Español' },
  { valor: 'ingles', etiqueta: 'Inglés' },
];

/**
 * Campos que viajan al guardar. Es la lista blanca de `config`: los que no
 * están (series, `tipo_doc`, `bonus`, las imágenes) son de sólo lectura.
 */
const CAMPOS_EDITABLES: readonly (keyof EmpresaFormulario)[] = [
  'ruc',
  'nom_comercial',
  'razon_social',
  'codigo_ubigeo',
  'direccion',
  'direccion_departamento',
  'direccion_departamento_id',
  'direccion_provincia',
  'direccion_provincia_id',
  'direccion_distrito',
  'direccion_distrito_id',
  'direccion_codigopais',
  'local',
  'web_emisor',
  'telefono',
  'email',
  'web_site_cpe',
  'web_site_cpe_todos',
  'direccion_impresora',
  'dir_1_empresa',
  'dir_2_empresa',
  'api_dir_empresa',
  'token',
  'dir_api_ubigeo',
  'api_dir_empresa_administrador',
  'token_administrador',
  'sucursal',
  'serie_guia_remision',
  'serie_cotizacion',
  'nci_sucursal',
  'fecha_vencimiento_certificado_digital',
  'api_almacen_id',
  'sumar_total_combustible_nota_venta',
  'precierre_turno',
  'generar_nota_venta',
  'transactions_limit',
  'closure',
  'legend_27037',
  'cuenta_bancaria_detraccion',
  'crd_boton_ultimo_turno',
  'crd_habilitar_campos_ingreso_manual_consulta_cliente',
  'dias_boleta_masiva',
  'precio_producto_boleta_masiva',
  'dias_maximos_anular',
  'configPastTransactions',
  'editMeters',
  'show_historic',
  'edit_payment',
  'login_mensaje_uno',
  'login_mensaje_dos',
  'descripcion_footer',
  'link_facebook',
  'link_twitter',
  'link_google',
  'color_fondo_login',
  'email_nombre',
  'email_usuario',
  'email_contrasena',
  'fidelity',
  'codigo_bonus',
  'bonus_subscription_code',
  'cardplus_auth_user',
  'cardplus_auth_pwd',
  'crd_servidor_alterno',
  'crd_url_principal',
  'crd_token_principal',
  'crd_url_secundario',
  'crd_token_secundario',
  'hermes_url',
  'hermes_user',
  'hermes_password',
  'hermes_equipment',
  'hermes_apikey',
  'ticketing_min_transactions',
  'ticketing_max_transactions',
  'monto_minimo_faltante',
  'ric_difference_red_threshold',
];

/** Arma el cuerpo del `PUT /api/empresa` con los campos editables. */
export function construirPayload(
  empresa: EmpresaConfig,
  app: ConfigCompanyPayload,
): EmpresaPayload {
  const campos: Record<string, unknown> = {};
  for (const campo of CAMPOS_EDITABLES) campos[campo] = empresa[campo];

  return {
    empresa: campos as unknown as EmpresaFormulario,
    config_company: { ...app },
  };
}

/** Campos de `config_company` que la pantalla edita (para el estado local). */
export function extrasDe(configCompany: ConfigCompany | null): ConfigCompanyPayload {
  return {
    igc_percentage: configCompany?.igc_percentage ?? 18,
    igv_exonerada: configCompany?.igv_exonerada ?? 0,
    showCredit: configCompany?.showCredit ?? 'ambos',
    mostrar_pago_adelantado: configCompany?.mostrar_pago_adelantado ?? 'ambos',
    tipo_documento_autoticketing: configCompany?.tipo_documento_autoticketing ?? 'boleta',
    mostrar_cardplus: configCompany?.mostrar_cardplus ?? 'menu',
    nfc_habilitado_pos: configCompany?.nfc_habilitado_pos ?? 0,
    loyaltyInMarket: configCompany?.loyaltyInMarket ?? 0,
    agrupar_productos_cpe_multiple: configCompany?.agrupar_productos_cpe_multiple ?? 0,
    max_free_transactions_per_shift: configCompany?.max_free_transactions_per_shift ?? 20,
    modo_trabajo: configCompany?.modo_trabajo ?? 'POS',
    modo_visualizacion_descuento: configCompany?.modo_visualizacion_descuento ?? 'NINGUNO',
    loyalty_code: configCompany?.loyalty_code ?? '',
    // Datos que consume la app.
    currency_type: configCompany?.currency_type ?? 'PEN',
    language: configCompany?.language ?? 'español',
    igv_tax_status: configCompany?.igv_tax_status ?? '',
    monto_maximo_anonimo: configCompany?.monto_maximo_anonimo ?? 0,
    loyalty_url_download: configCompany?.loyalty_url_download ?? '',
  };
}

/** Texto y tono del estado de la suscripción. */
export function estadoSuscripcion(plan: PlanContratado): {
  texto: string;
  tono: 'ok' | 'aviso' | 'error';
} {
  if (plan.dias_restantes === null) return { texto: 'Sin fecha de vencimiento', tono: 'aviso' };
  if (plan.dias_restantes < 0) {
    const dias = Math.abs(plan.dias_restantes);
    return { texto: `Vencido hace ${dias} ${dias === 1 ? 'día' : 'días'}`, tono: 'error' };
  }
  if (plan.dias_restantes <= 15) {
    return {
      texto: `Vence en ${plan.dias_restantes} ${plan.dias_restantes === 1 ? 'día' : 'días'}`,
      tono: 'aviso',
    };
  }

  return { texto: `${plan.dias_restantes} días restantes`, tono: 'ok' };
}

/** Precio de la suscripción según la periodicidad contratada. */
export function precioSuscripcion(plan: PlanContratado): string {
  const anual = plan.tipo === 'anual' && plan.precio_anual > 0;
  return formatCurrency(anual ? plan.precio_anual : plan.precio_mensual);
}

/** Precio de un plan disponible en la periodicidad indicada. */
export function precioPlan(plan: PlanDisponible, tipo: 'mensual' | 'anual'): string {
  const valor = tipo === 'anual' && plan.precio_anual > 0 ? plan.precio_anual : plan.precio_mensual;
  return formatCurrency(valor);
}

/** Texto de la cantidad de módulos del plan. */
export function textoModulos(total: number): string {
  return `${formatNumber(total)} ${total === 1 ? 'módulo' : 'módulos'}`;
}

/**
 * Límite del plan (usuarios o terminales).
 *
 * En la base, `0` significa **sin límite**: el panel anterior mostraba
 * «Ilimitados» en sus tarjetas de plan, no «0 usuarios».
 */
export function textoLimite(valor: number): string {
  return valor > 0 ? formatNumber(valor) : 'Ilimitados';
}

/**
 * Porcentaje del período de suscripción ya transcurrido (0..100).
 *
 * `null` cuando falta alguna fecha o el período no es válido, para poder omitir
 * la barra de vigencia en lugar de pintar algo sin sentido.
 */
export function progresoSuscripcion(plan: PlanContratado): number | null {
  if (!plan.fecha_inicio || !plan.fecha_fin) return null;

  const inicio = new Date(`${plan.fecha_inicio}T00:00:00`).getTime();
  const fin = new Date(`${plan.fecha_fin}T00:00:00`).getTime();

  if (!Number.isFinite(inicio) || !Number.isFinite(fin) || fin <= inicio) return null;

  return Math.min(100, Math.max(0, Math.round(((Date.now() - inicio) / (fin - inicio)) * 100)));
}

/** Convierte una bandera 0/1 al valor del `select` ('0' / '1'). */
export function aTextoBandera(valor: 0 | 1): '0' | '1' {
  return valor === 1 ? '1' : '0';
}

/** Convierte el valor de un `select` a bandera 0/1. */
export function aBandera(valor: string): 0 | 1 {
  return valor === '1' ? 1 : 0;
}
